import type { Tag } from 'lib-shared-types-api';
import { useMemo } from 'react';

import {
  IntegrationActionRunScalarItem,
  isIntegrationActionRunScalarItem,
} from 'apis/aiAgents';
import { aiAgentsApi } from 'apis/aiAgents/aiAgents.api';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

const MAX_SUGGESTIONS = 3;

type UseSuggestedTagsOptions = {
  /** The conversation id. */
  csid: number;
  /** The tags already applied to the conversation. */
  existingTags: readonly Tag[];
  /** Whether the conversation has any messages. */
  hasMessages: boolean;
};

type UseSuggestedTagsResult = {
  /** The suggested tags for the conversation. */
  suggestions: readonly SuggestedTag[];
  /** Whether the suggestions are being fetched. */
  isLoading: boolean;
  /** Whether the tag suggestions feature is enabled. */
  isEnabled: boolean;
};

export type SuggestedTag = {
  /** The id of the tag. */
  readonly id: string;
  /** The name of the tag. */
  readonly name: string;
  /** The color of the tag. */
  readonly color: string | null;
};

/**
 * A hook that returns suggested tags for a conversation.
 *
 * Suggestions are only fetched when:
 *
 * - The feature flag is enabled
 * - No tags are already applied
 * - The conversation has messages
 */
export function useSuggestedTags({
  csid,
  existingTags,
  hasMessages,
}: UseSuggestedTagsOptions): UseSuggestedTagsResult {
  const { tagSuggestionsEnabled } = useFeatureFlag(['tag-suggestions-enabled']);
  const shouldFetch = tagSuggestionsEnabled && hasMessages;
  const { data, isFetching } =
    aiAgentsApi.useCreateToolsIntegrationsActionsRunQuery(
      {
        integrationType: 'dixa',
        actionId: 'suggest_conversation_tags',
        csid,
      },
      {
        skip: !shouldFetch,
        selectFromResult: ({ data, ...rest }) => ({
          data:
            data?.items
              .filter((item): item is IntegrationActionRunScalarItem =>
                isIntegrationActionRunScalarItem(item),
              )
              .map(
                (item): SuggestedTag => ({
                  id: item.data.id,
                  name: item.data.name,
                  color: item.data.color,
                }),
              ) ?? [],
          ...rest,
        }),
      },
    );

  const suggestions = useMemo(
    () =>
      data
        ?.filter((tag) => !existingTags.some((t) => t.name === tag.name))
        .slice(0, MAX_SUGGESTIONS) ?? [],
    [data, existingTags],
  );

  return {
    suggestions,
    isLoading: isFetching,
    isEnabled: tagSuggestionsEnabled,
  };
}
